'use client';

import { SentryError } from '@ifixit/sentry';
import { createContext, use, useMemo, useState } from 'react';
import type { ComponentProps } from 'react';
import { cn } from '../lib/utils';
import { colorFromName, initials, isDarkColor } from './avatar';
import { BackMarketIcon } from './back-market-icon';
import { Badge } from './badge';
import {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuTrigger,
} from '@ifixit/ui/components/dropdown-menu';
import { FaIcon } from './icon';

interface User {
   username: string;
   handle: string | null;
   thumbnail: string | null;
   partner: 'Back Market' | 'THD' | null;
}

interface UserMenuContextValue {
   user: User;
}

const UserMenuContext = createContext<UserMenuContextValue | null>(null);

function useMenuContext() {
   const context = use(UserMenuContext);
   if (context == null) {
      throw new SentryError('useMenuContext must be used within a UserMenu');
   }
   return context;
}

export interface UserMenuProps extends ComponentProps<typeof DropdownMenu> {
   user: User;
}

/**
 * The signed-in user's avatar button and account menu in the site header,
 * shared by the Next.js and PHP headers so the two look the same.
 */
export function UserMenu({ user, ...otherProps }: UserMenuProps) {
   const value = useMemo((): UserMenuContextValue => ({ user }), [user]);

   return (
      <UserMenuContext value={value}>
         <DropdownMenu {...otherProps} />
      </UserMenuContext>
   );
}

interface UserMenuButtonProps extends ComponentProps<typeof DropdownMenuTrigger> {
   /** An id for the avatar inside the button; the trigger keeps the id the menu labels itself with. */
   avatarId?: string;
}

export function UserMenuButton({ avatarId, className, ...props }: UserMenuButtonProps) {
   return (
      <DropdownMenuTrigger
         className={cn(
            'inline-flex size-10 cursor-pointer items-center justify-center rounded-full outline-none transition-opacity duration-300 hover:opacity-70 focus-visible:ring-[3px] focus-visible:ring-brand-400/70 max-[376px]:ml-2',
            className
         )}
         {...props}
      >
         <UserAvatar id={avatarId} />
      </DropdownMenuTrigger>
   );
}

export function UserMenuContent({
   className,
   ...props
}: ComponentProps<typeof DropdownMenuContent>) {
   return (
      <DropdownMenuContent align="end" className={cn('min-w-56 shadow-md', className)} {...props} />
   );
}

export function UserMenuHeading({ className, ...props }: ComponentProps<'div'>) {
   const { user } = useMenuContext();
   return (
      <div className={cn('px-2 py-1.5 text-base', className)} {...props}>
         <p className="font-medium text-gray-900">{user.username}</p>
         {user.handle && <p className="text-gray-500">@{user.handle}</p>}
      </div>
   );
}

interface UserMenuLinkProps extends ComponentProps<'a'> {
   href: string;
   /** The unread count, already formatted for the locale; nothing shows when it's empty. */
   countLabel?: string;
}

export function UserMenuLink({ countLabel, className, children, ...props }: UserMenuLinkProps) {
   // The PHP pages color and underline every link in their base styles.
   return (
      <DropdownMenuItem asChild>
         <a className={cn('text-inherit no-underline', className)} {...props}>
            {children}
            {countLabel ? (
               <Badge className="ml-auto badge-brand rounded-full tabular-nums">{countLabel}</Badge>
            ) : null}
         </a>
      </DropdownMenuItem>
   );
}

function UserAvatar({ id }: { id?: string }) {
   const { user } = useMenuContext();
   const [thumbnailStatus, setThumbnailStatus] = useState<{ src: string; loaded: boolean } | null>(
      null
   );

   if (user.partner === 'Back Market') {
      return (
         <AvatarCircle id={id}>
            <BackMarketIcon className="size-full" />
         </AvatarCircle>
      );
   }

   if (user.thumbnail == null) {
      const background = colorFromName(user.username);
      return (
         <AvatarCircle
            id={id}
            className={cn(
               'text-[0.8rem] font-medium uppercase leading-none',
               isDarkColor(background) ? 'text-white' : 'text-gray-800'
            )}
            style={{ backgroundColor: background }}
         >
            {initials(user.username)}
         </AvatarCircle>
      );
   }

   const thumbnail = user.thumbnail;
   const isLoaded = thumbnailStatus?.src === thumbnail && thumbnailStatus.loaded;
   const hasFailed = thumbnailStatus?.src === thumbnail && !thumbnailStatus.loaded;

   return (
      <AvatarCircle id={id}>
         {!hasFailed && (
            <img
               src={thumbnail}
               alt=""
               width={32}
               height={32}
               className={cn('size-full object-cover', !isLoaded && 'hidden')}
               onLoad={() => setThumbnailStatus({ src: thumbnail, loaded: true })}
               onError={() => setThumbnailStatus({ src: thumbnail, loaded: false })}
            />
         )}
         {!isLoaded && <FaIcon name="user" className="size-4 text-gray-300" />}
      </AvatarCircle>
   );
}

function AvatarCircle({ className, ...props }: ComponentProps<'span'>) {
   return (
      <span
         className={cn(
            'flex size-8 items-center justify-center overflow-hidden rounded-full',
            className
         )}
         {...props}
      />
   );
}
